import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  GithubAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut,
  AuthError,
} from 'firebase/auth';

// Reuse the existing Firebase app instance (firebaseBattle.ts may have already init'd it)
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
export const firebaseAuth = getAuth(app);

// Google provider — requests email + profile
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');

// GitHub provider — requests email + basic read
const githubProvider = new GithubAuthProvider();
githubProvider.addScope('user:email');
githubProvider.addScope('read:user');

export interface OAuthUserInfo {
  email: string;
  displayName: string;
  photoURL?: string;
  uid: string;
}

export const signInWithGoogle = async (): Promise<OAuthUserInfo> => {
  const result = await signInWithPopup(firebaseAuth, googleProvider);
  const u = result.user;
  return {
    email: u.email ?? '',
    displayName: u.displayName ?? u.email?.split('@')[0] ?? 'User',
    photoURL: u.photoURL ?? undefined,
    uid: u.uid,
  };
};

export const signInWithGithub = async (): Promise<OAuthUserInfo> => {
  const result = await signInWithPopup(firebaseAuth, githubProvider);
  const u = result.user;
  // GitHub may not return email directly — it could be null for private emails
  const email = u.email ?? '';
  return {
    email,
    displayName: u.displayName ?? u.email?.split('@')[0] ?? 'User',
    photoURL: u.photoURL ?? undefined,
    uid: u.uid,
  };
};

export const signOutFirebase = (): Promise<void> => firebaseSignOut(firebaseAuth);

/** Maps Firebase AuthError codes to user-friendly messages */
export const getOAuthErrorMessage = (error: AuthError): string => {
  switch (error.code) {
    case 'auth/popup-closed-by-user':
      return 'Sign-in popup was closed. Please try again.';
    case 'auth/popup-blocked':
      return 'Pop-up blocked by your browser. Please allow pop-ups for this site.';
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email via a different sign-in method. Please use that method.';
    case 'auth/cancelled-popup-request':
      return 'Another sign-in is in progress.';
    case 'auth/operation-not-allowed':
      return 'This sign-in provider is not enabled yet. Please contact support.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';
    case 'auth/unauthorized-domain':
      return 'This domain is not authorised in Firebase. Add it in Firebase Console → Authentication → Settings.';
    default:
      return error.message ?? 'Authentication failed. Please try again.';
  }
};
